import * as BUSDToken from '../contracts/busd'
import { Big, BigSource } from 'big.js'
import { amountToWei } from '../utils/amount'
import { MAX_ALLOWANCE } from './useMasterContract'
import useMultiStepTx from './useMultiStepTx'
import { USDC_DECIMALS } from '../config'
import useNetworkId from './useNetworkId'
import { isMainnet } from '../constants'
import { Addressable, ethers, Interface, InterfaceAbi } from 'ethers'
import { getSigner, getWeb3Provider } from '@dynamic-labs/ethers-v6'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'

export interface IBUSDContract {
  balanceOf: (accountToCheck: string) => Promise<Big>
  approve: (spender: string, amount?: number) => Promise<void>
  allowance: (account: string, spender: string) => Promise<string | null>
  mint: (
    amount: BigSource,
    updateBalance: (account: string) => Promise<void>,
  ) => Promise<void>
}

async function useReadOnlyContract({
  address,
  abi,
}: {
  address: string | Addressable
  abi: Interface | InterfaceAbi
}) {
  return null
  const { primaryWallet } = useDynamicContext()
  if (!primaryWallet) return null
  const provider = await getWeb3Provider(primaryWallet)
  return new ethers.Contract(address, abi, provider)
}

async function useWriteOnlyContract({
  address,
  abi,
}: {
  address: string | Addressable
  abi: Interface | InterfaceAbi
}) {
  return null
  const { primaryWallet } = useDynamicContext()
  if (!primaryWallet) return null
  const signer = await getSigner(primaryWallet)
  return new ethers.Contract(address, abi, signer)
}

const useBUSDContract = (): IBUSDContract => {
  const { wrongNetwork } = useNetworkId()
  const { nextStepCallback } = useMultiStepTx()

  const readOnlyContract = useReadOnlyContract({
    address: BUSDToken.address,
    abi: BUSDToken.abi,
  })
  const writeOnlyContract = useWriteOnlyContract({
    address: BUSDToken.address,
    abi: BUSDToken.abi,
  })

  const { primaryWallet } = useDynamicContext()
  const { address } = primaryWallet ?? {}

  const balanceOf = async (accountToCheck: string) => {
    if (wrongNetwork) return null
    try {
      return await readOnlyContract['balanceOf'](accountToCheck)
    } catch (err) {
      console.error('Error in balanceOf function:', err)
      return err
    }
  }

  const approve = async (address: string, amount?: number) => {
    if (wrongNetwork) return
    try {
      const amountToSend = amount
        ? amountToWei(new Big(amount), USDC_DECIMALS).toString()
        : MAX_ALLOWANCE.toString()
      const gas = await writeOnlyContract?.['approve'].estimateGas(
        address,
        amountToSend,
      )

      await writeOnlyContract?.['approve'](address, amountToSend).send(
        { gas },
        nextStepCallback,
      )
    } catch (err) {
      console.error('Error in approve function:', err)
      return err
    }
  }

  const allowance = async (account: string, spender: string) => {
    if (wrongNetwork) return null
    try {
      return await readOnlyContract['allowance'](account, spender)
    } catch (err) {
      console.error('Error in allowance function:', err)
      return new Big(0)
    }
  }

  const mint = async (
    amount: BigSource,
    updateBalance: (account: string) => Promise<void>,
  ) => {
    if (wrongNetwork) return
    try {
      if (isMainnet) return

      const gas = await writeOnlyContract?.['mint'].estimateGas([
        address,
        amount.toString(),
      ])
      await writeOnlyContract?.['mint'](address, amount.toString()).send({
        gas,
      })

      updateBalance(address)
    } catch (err) {
      console.error('Error in mint function:', err)
      return err
    }
  }

  return { balanceOf, approve, mint, allowance }
}

export default useBUSDContract
